import { ArrowLeft, ExternalLink } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useGetAudioConversationDetailQuery } from '@/services/api/audioConversationService';

import { useClientCommunications } from '../ClientCommunicationsContext';
import { CallDetailPane } from './CallDetailPane';
import { EmailDetailPane } from './EmailDetailPane';

export const CommunicationDetailPane: React.FC = () => {
  const { selectedCommunication, selectCommunication, isDetailPaneOpen, matterId } =
    useClientCommunications();

  // Only fetch conversation detail for phone calls to get external_id for RingCentral link
  const conversationId = selectedCommunication?.audio_conversation_id || '';
  const { data: conversationDetail } = useGetAudioConversationDetailQuery(
    { matterId, conversationId },
    { skip: !conversationId || selectedCommunication?.type !== 'phone_call' },
  );

  // Cast to access external_id which may come from API but isn't in the base type
  const conversationDetailWithExternal = conversationDetail as
    | (typeof conversationDetail & { external_id?: string })
    | undefined;

  if (!isDetailPaneOpen || !selectedCommunication) {
    return null;
  }

  const handleBack = () => {
    selectCommunication(null);
  };

  const handleOpenInRingCentral = () => {
    if (conversationDetailWithExternal?.external_id) {
      window.open(
        `https://app.ringcentral.com/calls/${conversationDetailWithExternal.external_id}`,
        '_blank',
      );
    }
  };

  const renderContent = () => {
    switch (selectedCommunication.type) {
      case 'email':
        return <EmailDetailPane communication={selectedCommunication} />;
      case 'phone_call':
        return <CallDetailPane communication={selectedCommunication} />;
      default:
        return (
          <div className="flex items-center justify-center h-full text-zinc-500">
            <p>Detail view not available for this communication type</p>
          </div>
        );
    }
  };

  const showRingCentralButton =
    selectedCommunication.type === 'phone_call' && conversationDetailWithExternal?.external_id;

  return (
    <div
      className={cn(
        'flex flex-col h-full bg-white',
        'animate-in fade-in-0 duration-200',
      )}
    >
      {/* Header with back button and action buttons */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 bg-white">
        <Button
          variant="ghost"
          size="sm"
          className="gap-2 font-semibold text-zinc-700 hover:text-zinc-900 -ml-2"
          onClick={handleBack}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>

        {/* Action buttons */}
        {showRingCentralButton && (
          <Button
            variant="outline"
            size="sm"
            className="gap-2 text-sm font-semibold"
            onClick={handleOpenInRingCentral}
          >
            <ExternalLink className="h-4 w-4" />
            Open in RingCentral
          </Button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">{renderContent()}</div>
    </div>
  );
};

export default CommunicationDetailPane;
